import { finalizeSupply } from "../phases/supply-phase";
import { getCardInstance } from "../selectors/card-instances";
import { getSupplySnapshot } from "../selectors/supply";
import type {
  CurrentCoreAlphaGameState,
  DiscardArmyForUpkeepAction,
  GameEvent,
  LaneState,
} from "../types";

export interface DiscardArmyForUpkeepTransitionResult {
  readonly state: CurrentCoreAlphaGameState;
  readonly events: readonly GameEvent[];
}

function eventId(actionSequence: number, ordinal: number): string {
  return `event-${actionSequence}-${ordinal}`;
}

export function applyDiscardArmyForUpkeep(
  state: Readonly<CurrentCoreAlphaGameState>,
  action: DiscardArmyForUpkeepAction,
  actionSequence: number,
): DiscardArmyForUpkeepTransitionResult {
  const lane = state.lanes[action.laneId];
  const side = lane.players[action.playerId];
  const instance = getCardInstance(state, action.armyCardInstanceId);
  if (instance === undefined) {
    throw new Error("Validated Upkeep discard unexpectedly lost its card-instance record.");
  }

  const updatedLane: LaneState = {
    ...lane,
    players: {
      ...lane.players,
      [action.playerId]: {
        ...side,
        armies: side.armies.filter((army) => army.cardInstanceId !== action.armyCardInstanceId),
      },
    },
  };
  const discardedState: CurrentCoreAlphaGameState = {
    ...state,
    lanes: { ...state.lanes, [action.laneId]: updatedLane },
    players: {
      ...state.players,
      [action.playerId]: {
        ...state.players[action.playerId],
        discard: [...state.players[action.playerId].discard, action.armyCardInstanceId],
      },
    },
  };
  const events: GameEvent[] = [
    {
      type: "army-discarded-for-upkeep",
      eventId: eventId(actionSequence, 0),
      actionSequence,
      playerId: action.playerId,
      laneId: action.laneId,
      armyCardInstanceId: action.armyCardInstanceId,
      armyCardDefinitionId: instance.cardDefinitionId,
    },
  ];
  const snapshot = getSupplySnapshot(discardedState);

  if (snapshot.unsustainableLaneIds.length === 0) {
    const finalized = finalizeSupply(discardedState, snapshot, actionSequence, events.length);
    if (finalized.state.schemaVersion === 5) {
      const actionWindowEvent: GameEvent = {
        type: "action-window-opened",
        eventId: eventId(actionSequence, events.length + finalized.events.length),
        actionSequence,
        playerId: action.playerId,
        turnNumber: finalized.state.turnNumber,
      };
      return {
        state: { ...finalized.state, phase: "action-window" },
        events: [...events, ...finalized.events, actionWindowEvent],
      };
    }
    return { state: finalized.state, events: [...events, ...finalized.events] };
  }

  const updatedSupplyLane = snapshot.lanes[action.laneId];
  if (!updatedSupplyLane.sustainable) {
    events.push({
      type: "upkeep-shortfall-detected",
      eventId: eventId(actionSequence, events.length),
      actionSequence,
      playerId: action.playerId,
      laneId: action.laneId,
      shortfall: updatedSupplyLane.shortfall,
      armyCardInstanceIds: updatedSupplyLane.armyCardInstanceIds,
    });
  }

  return { state: discardedState, events };
}
